import { createContext, useState, useContext, useRef } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";

import { Button } from "common/ui/Button";
import { Paragraph } from "common/ui/Paragraph";
import { Icon } from "common/ui/Icon";

import { useClickOutside } from "common/middleware/hooks";

import { formatDateMonthYear } from "common/utils/utils";

import styles from "./Notifications.module.scss";

const NotificationsContext = createContext<{
  closeNotifications: () => void,
}>({
  closeNotifications: undefined,
});

export const useNotifications = () => useContext(NotificationsContext);

interface NotificationsProps {
  icon: React.ReactNode,
  totalNumOfNotifications: number,
  children: React.ReactNode[],
  action: {
    label: string,
    onClick: () => Promise<void> | void,
    isLoading?: boolean,
    isDisabled?: boolean,
  },
  noNotificationsMessage?: string,
}

export function Notifications({
  icon,
  totalNumOfNotifications,
  children,
  action,
  noNotificationsMessage,
}: NotificationsProps) {
  const { t } = useTranslation("common");

  const [isNotificationOpen, setIsNotificationOpen] = useState(false);

  const notificationsContainer = useRef(null);

  useClickOutside(notificationsContainer, () => setIsNotificationOpen(false));

  return (
    <div
      ref={notificationsContainer}
      className={styles.Notifications}
    >
      <button
        className={styles.IconContainer}
        onClick={() => setIsNotificationOpen(!isNotificationOpen)}
      >
        {icon}
        {totalNumOfNotifications > 0 && (
          <span className={styles.Count}>
            {totalNumOfNotifications}
          </span>
        )}
      </button>
      <NotificationsContext.Provider
        value={{ closeNotifications: () => setIsNotificationOpen(false) }}
      >
        {isNotificationOpen && (
          <div className={styles.ListContainer}>
            {children?.length > 0 ? (
              <div className={styles.List}>
                {children}
              </div>
            ) : (
              <Paragraph variant="p1" className={styles.NoNotifications}>
                {noNotificationsMessage || t("notifications.noNotifications")}
              </Paragraph>
            )}
            <Button
              className={styles.ActionButton}
              variant="tertiary"
              onClick={async () => {
                await action.onClick();
                setIsNotificationOpen(false);
              }}
              isLoading={action.isLoading}
              disabled={action.isDisabled}
            >
              {action.label}
            </Button>
          </div>
        )}
      </NotificationsContext.Provider>
    </div>
  )
}

interface NotificationsItemProps {
  href?: string,
  onClick?: () => void | Promise<void>,
  date: Date,
  subject: string,
  message: string,
  clearNotification: () => Promise<void>,
}

const NotificationsItem = ({
  href,
  onClick,
  date,
  subject,
  message,
  clearNotification,
}: NotificationsItemProps) => {
  const { closeNotifications } = useNotifications();

  const [isClearingNotification, setIsClearingNotification] = useState(false);

  return (
    <div className={styles.Item}>
      {href ? (
        <Link
          className={styles.Link}
          href={href}
          onClick={async () => {
            if (onClick) {
              await onClick();
            }

            closeNotifications();
          }}
        >
          <NotificationsItemContent
            date={date}
            subject={subject}
            message={message}
          />
        </Link>
      ) : (
        <button
          className={styles.Link}
          onClick={async () => {
            if (onClick) {
              await onClick();
            }

            closeNotifications();
          }}
        >
          <NotificationsItemContent
            date={date}
            subject={subject}
            message={message}
          />
        </button>
      )}
      <button
        onClick={async () => {
          setIsClearingNotification(true);
          await clearNotification();
          setIsClearingNotification(false);
        }}
        disabled={isClearingNotification}
      >
        <Icon icon="closeMinor" />
      </button>
    </div>
  )
}

Notifications.Item = NotificationsItem;

interface NotificationsItemContentProps {
  date: Date,
  subject: string,
  message: string,
}

const NotificationsItemContent = ({
  date,
  subject,
  message,
}: NotificationsItemContentProps) => {
  const { locale } = useRouter();

  return (
    <>
      <Paragraph variant="p2">
        {formatDateMonthYear(locale, date)} | {date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
      </Paragraph>
      <Paragraph variant="p3">
        {subject}
      </Paragraph>
      <Paragraph variant="p1">
        {message}
      </Paragraph>
    </>
  )
}
